/**
 * DemoLoopSlot — muted, looping demo video on the Login hero.
 *
 * Plays from `/demo-loop.mp4` (drop a screen recording into
 * `/app/frontend/public/`). When the asset isn't present yet, renders
 * a styled placeholder card instead of an empty white box so the slot
 * doesn't collapse the layout. Autoplays only when muted (browser
 * policy), and we set `playsInline` so iOS Safari doesn't fullscreen it.
 *
 * Resilience: once a HEAD check confirms the asset is a valid video
 * response, we treat the file as present even if the `<video>` element
 * fires a transient `error` event (e.g. flaky range request, codec
 * pipeline restart). Without this guard, one bad event would flip the
 * slot to the placeholder permanently.
 */
import { useEffect, useRef, useState } from "react";
import { PlayCircle } from "lucide-react";

const VIDEO_SRC = "/demo-loop.mp4";

export function DemoLoopSlot() {
  const videoRef = useRef(null);
  const [hasVideo, setHasVideo] = useState(true); // optimistic: try to load
  const [headOk, setHeadOk] = useState(false); // confirmed by HEAD check

  useEffect(() => {
    // HEAD-check the asset. If it 404s (or the ingress rewrites to the
    // SPA fallback returning HTML), fall through to the placeholder card.
    // Also accept any video/* content-type — both `video/mp4` and
    // application/octet-stream are commonly served by static handlers.
    fetch(VIDEO_SRC, { method: "HEAD" })
      .then((r) => {
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        if (!r.ok) { setHasVideo(false); return; }
        if (ct.startsWith("text/html")) { setHasVideo(false); return; }
        // mp4, video/*, octet-stream → treat as valid.
        setHeadOk(true);
      })
      .catch(() => setHasVideo(false));
  }, []);

  if (!hasVideo) {
    return (
      <div
        className="mt-7 rounded-xl border border-dashed border-white/15 bg-white/[0.03] p-5"
        data-testid="demo-loop-placeholder"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-ek-purple/20 p-2 text-ek-lilac">
            <PlayCircle size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ek-lilac">
              Demo loop · placeholder
            </p>
            <p className="mt-1 text-xs font-light text-white/55">
              Drop a 30-second screen recording at{" "}
              <code className="rounded bg-white/10 px-1 py-0.5 font-mono text-[10px] text-white/75">
                /demo-loop.mp4
              </code>
              {" "}— it auto-plays here, muted and looped.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mt-7 overflow-hidden rounded-xl border border-white/10 bg-black shadow-[0_8px_30px_rgba(119,55,246,0.25)]"
      data-testid="demo-loop-video"
    >
      <video
        ref={videoRef}
        src={VIDEO_SRC}
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        className="block aspect-video w-full"
        onError={() => {
          // Only fall back to the placeholder if the HEAD check hasn't
          // already vouched for the asset — otherwise a transient
          // codec-pipeline error would permanently hide a valid video.
          if (!headOk) setHasVideo(false);
        }}
      />
    </div>
  );
}
